interface IconProps {
  size?: number
  className?: string
}

const base = (size: number, className?: string) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className,
  'aria-hidden': true,
})

export const IconBrief = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </svg>
)

export const IconScript = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M7 4h7l4 4v11a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
    <path d="M13 4v4h4" />
    <path d="M9 13h6M9 16.5h4" />
  </svg>
)

export const IconTeleprompter = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M7 8.5h10M7 11.5h6" />
    <path d="M9 20h6M12 16v4" />
  </svg>
)

export const IconCaption = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M7 11h2M12 11h5M7 14.5h5M14.5 14.5h2.5" />
  </svg>
)

export const IconPin = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
)

export const IconSliders = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M5 7h9M18 7h1M5 12h2M11 12h8M5 17h6M15 17h4" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="9" cy="12" r="2" />
    <circle cx="13" cy="17" r="2" />
  </svg>
)

export const IconSunrise = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M3 18h18M12 3v4M5.6 9.6 4.2 8.2M18.4 9.6l1.4-1.4M2 14h2M20 14h2" />
    <path d="M8 14a4 4 0 0 1 8 0" />
  </svg>
)

export const IconSparkPen = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M5 19l9.5-9.5a2 2 0 0 0 0-2.8l-.2-.2a2 2 0 0 0-2.8 0L2 16v3h3Z" />
    <path d="M18 4l.7 1.8L20.5 6.5l-1.8.7L18 9l-.7-1.8L15.5 6.5l1.8-.7L18 4Z" />
  </svg>
)

export const IconCamera = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
    <circle cx="12" cy="13" r="3.2" />
  </svg>
)

/** Film strip, for the recording library. */
export const IconFilm = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M7 5v14M17 5v14M3 12h18M3 8.5h4M3 15.5h4M17 8.5h4M17 15.5h4" />
  </svg>
)

export const IconArrow = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

export const IconStar = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M12 4l2.3 4.9 5.2.7-3.8 3.7.9 5.2L12 16.8 7.4 18.2l.9-5.2L4.5 9.6l5.2-.7L12 4Z" />
  </svg>
)

export const IconMenu = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
)

export const IconX = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
)

export const IconUndo = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M9 7L4 12l5 5M4 12h11a5 5 0 0 1 0 10h-1" />
  </svg>
)

export const IconCopy = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h8" />
  </svg>
)

/** A struck coin, for the rewards case. Two rings and a milled edge, which is
 *  what reads as "coin" at 18px where a star reads as "favourite". */
export const IconCoin = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="5" />
    <path d="M12 3.5v1.6M12 18.9v1.6M3.5 12h1.6M18.9 12h1.6" />
  </svg>
)

export const IconUser = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <circle cx="12" cy="8" r="3.6" />
    <path d="M5 19.5a7 7 0 0 1 14 0" />
  </svg>
)

export const IconChevronRight = ({ size = 24, className }: IconProps) => (
  <svg {...base(size, className)}>
    <path d="M9.5 5.5 16 12l-6.5 6.5" />
  </svg>
)
